import { z } from "zod";

/**
 * Таблица на доске урока (`docs/board-table-design.md`): потолки, поля схемы,
 * id строк и столбцов, правка структуры и чтение текста клеток.
 *
 * Устройство одной строкой (3.1 и 3.2 проекта): в элементе задания `kind:
 * "table"` лежит только СТРУКТУРА (id строк, id и веса столбцов, флаг шапки),
 * а текст клеток живёт отдельным ключом верхнего уровня `TABLES_KEY`, по
 * `Y.Text` на клетку под ключом `строка.столбец`. Текст в элементе бывает
 * только в снимке архива (`cells`), и снимок его ЗАМЕНЯЕТ.
 *
 * ⚠️ Сама схема собирается в `./exercises.ts`, а не здесь: ей нужны общие
 * поля `base` (id, рамка, привязка к странице), а они живут там. Импорт
 * оттуда значением замкнул бы кольцо модулей, поэтому отсюда уезжают только
 * поля и проверка формы, а сам элемент (`TableElement`) экспортирует
 * `./exercises.ts`.
 *
 * Шаги клавиш и план вставки блока живут в соседнем `./table-plan.ts`, доступ
 * к слою клеток в `./table-layer.ts`.
 */

/**
 * Потолки таблицы (4.8 проекта).
 *
 * ⚠️ **Б6. Первые пять стоят в схеме и после выкатки 1 можно только
 * поднимать**, а `TableId` только ослаблять. Опущенный потолок делает уже
 * записанные таблицы непроходящими схему: живая доска молча её не нарисует,
 * снимок молча выбросит и затрёт прошлый, а архив с уже снятым `cells` ответит
 * 500 целиком (`BoardSnapshotResponse`). Свободно правится только
 * `TABLE_TEXT_MAX`: в схеме его нет, его держит `tableCellsOf`.
 */
export const TABLE_MAX_ROWS = 40;
export const TABLE_MAX_COLS = 12;
/** Вес ширины столбца, 1-6: рамку растягивает преподаватель уголком. */
export const TABLE_COL_W_MAX = 6;
export const TABLE_MAX_CELLS = 400;
/** Знаков в клетке (Р9, вариант б, ответ владельца 02.11.2026). */
export const TABLE_CELL_MAX = 500;
/** Знаков на таблицу. В схеме его нет намеренно, разбор у `tableCellsOf`. */
export const TABLE_TEXT_MAX = 20000;

/** Длина нового id строки или столбца. Схема пускает до 12 (`TableId`). */
export const TABLE_ID_LENGTH = 6;

/**
 * Id строки или столбца. Без `#` и точки: точка разделяет части ключа клетки,
 * а `#` это ключ метки у ответов (`COLUMN_MARK_KEY`).
 */
export const TableId = z.string().regex(/^[a-z0-9]{1,12}$/);

export const TableCol = z.object({ id: TableId, w: z.number().int().min(1).max(TABLE_COL_W_MAX) });
export type TableCol = z.infer<typeof TableCol>;

/** Поля элемента таблицы сверх общих. Схему собирает `./exercises.ts`. */
export const TABLE_ELEMENT_FIELDS = {
  kind: z.literal("table"),
  rows: z.array(TableId).min(1).max(TABLE_MAX_ROWS),
  cols: z.array(TableCol).min(1).max(TABLE_MAX_COLS),
  head: z.boolean().default(false),
  /** Только в снимке архива (раздел 6 проекта). В живом документе НЕТ. */
  cells: z.record(z.string(), z.string().max(TABLE_CELL_MAX)).optional(),
};

/**
 * Форма, которую схема проверяет поверх полей: id внутри строк и внутри
 * столбцов не повторяются (ключ клетки иначе делили бы две клетки), и клеток
 * не больше `TABLE_MAX_CELLS`.
 */
export function tableShapeValid(table: {
  rows: readonly string[];
  cols: readonly { id: string }[];
}): boolean {
  return (
    new Set(table.rows).size === table.rows.length &&
    new Set(table.cols.map((col) => col.id)).size === table.cols.length &&
    table.rows.length * table.cols.length <= TABLE_MAX_CELLS
  );
}

/** Структура таблицы без рамки и id элемента: то, что правит преподаватель. */
export interface TableStructure {
  rows: readonly string[];
  cols: readonly TableCol[];
  head: boolean;
}

/** Ключ клетки в карте таблицы слоя `TABLES_KEY`. */
export function tableCellKey(row: string, col: string): string {
  return `${row}.${col}`;
}

/** Ключи всех клеток структуры в порядке чтения: строки, внутри столбцы. */
export function tableKeys(table: { rows: readonly string[]; cols: readonly { id: string }[] }): string[] {
  const keys: string[] = [];
  for (const row of table.rows) for (const col of table.cols) keys.push(tableCellKey(row, col.id));
  return keys;
}

/** Источник случайных байтов. Подставляется в тестах. */
export type RandomBytes = (length: number) => Uint8Array;

const cryptoBytes: RandomBytes = (length) =>
  globalThis.crypto.getRandomValues(new Uint8Array(length));

const ID_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";
const ID_ATTEMPTS = 64;

/**
 * Новый id строки или столбца: шесть знаков `[a-z0-9]`, свободный в `taken`.
 *
 * ⚠️ **Свой генератор, а не `randomExerciseId`**: тот даёт 12 знаков из
 * алфавита с `A-Z`, `-` и `_`, и схема `TableId` такой id не пропустит.
 *
 * ⚠️ **Тянет новый, пока не выйдет свободный.** Совпадение двух id внутри
 * таблицы схема не пропускает, и таблица выпала бы из схемы целиком. Шанс около
 * 6·10⁻⁷ на таблицу при 52 id, но цена это потеря таблицы (решение главной
 * сессии 02.11.2026). Проверки по локальной структуре хватает: элемент пишется
 * целиком, и id чужой вкладки в итоговую структуру не попадают. Потолок
 * попыток только от сломанного источника случайности: без него вкладка
 * зависла бы навсегда.
 */
export function newTableId(taken: ReadonlySet<string>, random: RandomBytes = cryptoBytes): string {
  for (let attempt = 0; attempt < ID_ATTEMPTS; attempt += 1) {
    let id = "";
    for (const byte of random(TABLE_ID_LENGTH)) id += ID_ALPHABET.charAt(byte % ID_ALPHABET.length);
    if (!taken.has(id)) return id;
  }
  throw new Error("table id: random source keeps repeating taken ids");
}

/**
 * Структура новой таблицы `cols × rows` со свежими id. Размер прижимается к
 * потолкам: сначала столбцы, затем строки по `TABLE_MAX_CELLS` (4.8 проекта).
 */
export function newTable(cols: number, rows: number, random: RandomBytes = cryptoBytes): TableStructure {
  const colCount = Math.min(Math.max(1, Math.floor(cols)), TABLE_MAX_COLS);
  const rowCount = Math.min(
    Math.max(1, Math.floor(rows)),
    TABLE_MAX_ROWS,
    Math.floor(TABLE_MAX_CELLS / colCount),
  );
  const taken = new Set<string>();
  const fresh = (): string => {
    const id = newTableId(taken, random);
    taken.add(id);
    return id;
  };
  const colIds = Array.from({ length: colCount }, fresh);
  return {
    rows: Array.from({ length: rowCount }, fresh),
    cols: colIds.map((id) => ({ id, w: 1 })),
    head: false,
  };
}

/**
 * Правка структуры.
 *
 * `after` это id соседа, ПОСЛЕ которого встаёт строка или столбец; `null` это
 * начало. Возврат кладёт на место после запомненного соседа, а не по индексу:
 * структура могла измениться (3.5 проекта). Соседа больше нет: в конец.
 * `heal` структуру не меняет и только дозаводит недостающие ключи.
 */
export type TableChange =
  | { op: "addRow"; after: string | null }
  | { op: "addCol"; after: string | null }
  | { op: "removeRow"; id: string }
  | { op: "removeCol"; id: string }
  | { op: "restoreRow"; id: string; after: string | null }
  | { op: "restoreCol"; col: TableCol; after: string | null }
  | { op: "head"; on: boolean }
  | { op: "grow"; rows: number; cols: number }
  | { op: "heal" };

export interface TableChangeResult {
  structure: TableStructure;
  /** Id заведённых строк и столбцов, в порядке заведения. */
  added: string[];
  /** Ключи клеток, которые завести: в итоговой структуре и не живые по `live`. */
  prepare: string[];
  /** Ключи клеток, которые удалить: ушли из структуры и живые по `live`. */
  cleanup: string[];
}

/**
 * Чистая правка структуры: от структуры, правки и набора живых ключей слоя
 * отдаёт новую структуру и два списка ключей (3.2 проекта). Карты Yjs пишет
 * писатель через доступ слоя.
 *
 * ⚠️ **`live` берётся только у доступа (`TableLayer.live`), а не по карте.**
 * При отдельном документе слоя карта до сброса ещё держит ключи, ждущие
 * удаления, и дозаведение по карте не завело бы их снова.
 *
 * ⚠️ **Дозаведение на КАЖДОЙ записи**: `prepare` это все ключи итоговой
 * структуры, которых нет в `live`, а не только ключи новой строки. Так
 * оживает мёртвый столбец после гонки двух вкладок (3.4 проекта). Списки
 * «завести» и «удалить» не пересекаются по построению.
 *
 * `null` это правка, которой сделать нельзя: последнюю строку или столбец не
 * убрать, потолок, неизвестный id, возврат id, который уже стоит.
 */
export function applyTableChange(
  structure: TableStructure,
  change: TableChange,
  live: ReadonlySet<string>,
  random: RandomBytes = cryptoBytes,
): TableChangeResult | null {
  const next = changed(structure, change, random);
  if (next === null) return null;
  const keys = new Set(tableKeys(next.structure));
  return {
    structure: next.structure,
    added: next.added,
    prepare: [...keys].filter((key) => !live.has(key)),
    cleanup: tableKeys(structure).filter((key) => !keys.has(key) && live.has(key)),
  };
}

function fits(rows: number, cols: number): boolean {
  return rows <= TABLE_MAX_ROWS && cols <= TABLE_MAX_COLS && rows * cols <= TABLE_MAX_CELLS;
}

/** Место вставки после соседа: `null` это начало, пропавший сосед это конец. */
function slotAfter(ids: readonly string[], after: string | null): number {
  if (after === null) return 0;
  const at = ids.indexOf(after);
  return at < 0 ? ids.length : at + 1;
}

function inserted<T>(list: readonly T[], at: number, item: T): T[] {
  return [...list.slice(0, at), item, ...list.slice(at)];
}

function changed(
  table: TableStructure,
  change: TableChange,
  random: RandomBytes,
): { structure: TableStructure; added: string[] } | null {
  const colIds = table.cols.map((col) => col.id);
  const taken = new Set([...table.rows, ...colIds]);
  const same = { structure: table, added: [] };
  switch (change.op) {
    case "heal":
      return same;
    case "head":
      return { structure: { ...table, head: change.on }, added: [] };
    case "addRow": {
      if (change.after !== null && !table.rows.includes(change.after)) return null;
      if (!fits(table.rows.length + 1, table.cols.length)) return null;
      const id = newTableId(taken, random);
      const rows = inserted(table.rows, slotAfter(table.rows, change.after), id);
      return { structure: { ...table, rows }, added: [id] };
    }
    case "addCol": {
      if (change.after !== null && !colIds.includes(change.after)) return null;
      if (!fits(table.rows.length, table.cols.length + 1)) return null;
      const id = newTableId(taken, random);
      const at = slotAfter(colIds, change.after);
      // Вес берётся у соседа слева (у первого столбца при вставке в начало):
      // столбец рядом с широким скорее всего про то же
      const w = (table.cols[Math.max(at - 1, 0)] ?? { w: 1 }).w;
      return { structure: { ...table, cols: inserted(table.cols, at, { id, w }) }, added: [id] };
    }
    case "removeRow": {
      if (table.rows.length <= 1 || !table.rows.includes(change.id)) return null;
      return { structure: { ...table, rows: table.rows.filter((id) => id !== change.id) }, added: [] };
    }
    case "removeCol": {
      if (table.cols.length <= 1 || !colIds.includes(change.id)) return null;
      return {
        structure: { ...table, cols: table.cols.filter((col) => col.id !== change.id) },
        added: [],
      };
    }
    case "restoreRow": {
      if (taken.has(change.id) || !TableId.safeParse(change.id).success) return null;
      if (!fits(table.rows.length + 1, table.cols.length)) return null;
      const rows = inserted(table.rows, slotAfter(table.rows, change.after), change.id);
      return { structure: { ...table, rows }, added: [change.id] };
    }
    case "restoreCol": {
      if (taken.has(change.col.id) || !TableCol.safeParse(change.col).success) return null;
      if (!fits(table.rows.length, table.cols.length + 1)) return null;
      const cols = inserted(table.cols, slotAfter(colIds, change.after), change.col);
      return { structure: { ...table, cols }, added: [change.col.id] };
    }
    case "grow": {
      const addRows = Math.max(0, Math.floor(change.rows));
      const addCols = Math.max(0, Math.floor(change.cols));
      if (!fits(table.rows.length + addRows, table.cols.length + addCols)) return null;
      const added: string[] = [];
      const fresh = (): string => {
        const id = newTableId(taken, random);
        taken.add(id);
        added.push(id);
        return id;
      };
      const w = (table.cols[table.cols.length - 1] ?? { w: 1 }).w;
      const cols = [...table.cols, ...Array.from({ length: addCols }, () => ({ id: fresh(), w }))];
      const rows = [...table.rows, ...Array.from({ length: addRows }, fresh)];
      return { structure: { ...table, rows, cols }, added };
    }
  }
}

/**
 * Обрезка до `max` кодов UTF-16 с отбросом одинокой старшей половинки
 * суррогатной пары на срезе. Своя, а не из collab: тот `clamp` локален в
 * `apps/collab/src/snapshot.ts` и в общий пакет не едет (4.8 проекта).
 * Почему половинка опасна, разобрано там и в пункте 1б
 * `docs/engineering-standards.md`.
 */
export function clampTableText(value: string, max: number): string {
  if (value.length <= max) return value;
  if (max <= 0) return "";
  const cut = value.slice(0, max);
  const last = cut.charCodeAt(max - 1);
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
}

/**
 * Текст клеток таблицы из записей слоя: то, что снимок кладёт в `cells`, и то,
 * чем текст читает веб. `null` это «текста нет», и тогда `cells` у таблицы
 * нет вовсе.
 *
 * Берутся записи слоя, а не документ (3.2 проекта): `yjs` в общий пакет не
 * едет, `Y.Text` в строку приводит вызывающий. Запись не строкой пропускается
 * (чужая форма), клетка из пробелов это пустая клетка.
 *
 * ⚠️ **Общий потолок текста держит ЭТА функция**, а не только писатель
 * вставки: слияние офлайн-правок даёт таблицу сверх любого потолка, а схема
 * общего потолка не знает намеренно (иначе такая таблица выпала бы из снимка
 * или уронила архив). Обход в порядке структуры, каждая клетка до
 * `TABLE_CELL_MAX`, сумма до `TABLE_TEXT_MAX`. Клетка на границе режется до
 * остатка, а не выбрасывается (её начало человек видел первым), и дальше
 * клетки не берутся. Сироты (ключи не из структуры) не читаются вовсе.
 */
export function tableCellsOf(
  entries: Iterable<readonly [string, unknown]>,
  table: { rows: readonly string[]; cols: readonly { id: string }[] },
): Record<string, string> | null {
  const texts = new Map<string, unknown>(entries);
  const out: Record<string, string> = {};
  let left = TABLE_TEXT_MAX;
  let found = false;
  for (const key of tableKeys(table)) {
    const raw = texts.get(key);
    if (typeof raw !== "string" || raw.trim() === "") continue;
    const whole = clampTableText(raw, TABLE_CELL_MAX);
    const text = clampTableText(whole, left);
    if (text === "") break;
    out[key] = text;
    found = true;
    left -= text.length;
    if (text.length < whole.length || left <= 0) break;
  }
  return found ? out : null;
}
